import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostBinding,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatListModule } from '@angular/material/list';
import { ActivatedRoute } from '@angular/router';
import { Rive } from '@rive-app/webgl2';
import {
  AudioTrack,
  AudioTrackComponent,
} from 'shared/src/components/audio-track/audio-track.component';
import { SocialProofComponent } from 'shared/src/components/social-proof/social-proof.component';
import { UserReviewComponent } from 'shared/src/components/user-review/user-review.component';
import { AnalyticsService } from '../../../../../shared/src/services/analytics.service';
import { fadeAnimation, slideInAnimation } from '../../animations/route-animations';
import { Quiz } from '../../data/quiz';
import { FunnelFlowService } from '../../services/funnel-flow.service';
import { AiChatComponent } from '../ai-chat/ai-chat.component';
import { MentionedInComponent } from '../mentioned-in/mentioned-in.component';
import { QuizListOptionComponent } from '../quiz-list-option/quiz-list-option.component';
import { TextPlayerComponent } from '../text-player/text-player.component';

@Component({
  selector: 'app-funnel-quiz-step',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatListModule,
    MatChipsModule,
    MatButtonModule,
    MatFormFieldModule,
    FormsModule,
    AudioTrackComponent,
    UserReviewComponent,
    SocialProofComponent,
    QuizListOptionComponent,
    AiChatComponent,
    TextPlayerComponent,
    MentionedInComponent,
  ],
  templateUrl: './quiz-step.component.html',
  styleUrls: ['./quiz-step.component.scss'],
  animations: [fadeAnimation, slideInAnimation],
})
export class QuizStepComponent implements OnInit, AfterViewInit, OnDestroy {
  data!: Quiz;
  form: FormGroup;

  currentIndex: number = 0;
  progress = 0;

  isTypingMessagePlayed = false;

  footerMode: 'terms' | 'address' = 'address';

  private riveInstance: Rive | undefined;

  // Voting and playback state for text demos
  hasStartedTextDemo = false;

  @HostBinding('@routeAnimations') get routeAnimationData() {
    return this.currentIndex;
  }

  @ViewChildren('audioTrackRef') audioTracks!: QueryList<AudioTrackComponent>;
  @ViewChild('animation') riveAnimation!: ElementRef;
  @ViewChild(TextPlayerComponent) textPlayer!: TextPlayerComponent;

  constructor(
    private route: ActivatedRoute,
    private flowService: FunnelFlowService,
    private fb: FormBuilder,
    private analyticsService: AnalyticsService
  ) {
    this.form = this.fb.group({ selectedOptions: [''] });
  }

  ngOnInit(): void {
    const indexParam = this.route.snapshot.paramMap.get('index') || '0';

    this.currentIndex = parseInt(indexParam, 10);
    const data = this.flowService.getQuizData(this.currentIndex);

    if (!data) {
      this.flowService.goToStart();
      return;
    }

    this.data = data;
    this.progress = this.flowService.getProgress(this.currentIndex);
    this.footerMode = this.flowService.isFirstQuizStep(this.currentIndex) ? 'terms' : 'address';
  }

  ngAfterViewInit(): void {
    this.checkAnimation();
  }

  ngOnDestroy(): void {
    if (this.riveInstance) {
      this.riveInstance.stop();
      this.riveInstance.cleanup();
      this.riveInstance = undefined;
    }
  }

  buttonClicked(): void {
    const selectedOptions = this.form.value.selectedOptions;
    this.finish(selectedOptions);
  }

  voteClicked(choice: 'yes' | 'no' | 'miss'): void {
    this.finish([choice]);
  }

  optionClicked(): void {
    if (this.data.isMultiple) {
      return;
    }

    this.finish(this.form.value.selectedOptions);
  }

  onPlay(currentTrack: AudioTrackComponent, track: AudioTrack): void {
    this.analyticsService.track('web_audio_demo_played', {
      title: track.title,
      asset_id: this.data.assetId,
    });

    this.audioTracks.forEach(trackComponent => {
      if (trackComponent !== currentTrack) {
        trackComponent.pause();
      }
    });

    this.flowService.rememberLastPlayedTrack(track);
  }

  getBackgroundImageUrl(): string {
    if (this.data.imageConfig?.path) {
      return `url(${encodeURIComponent(this.data.imageConfig.path)})`;
    }

    if (this.data.rive?.backgroundImagePath) {
      return `url(${this.data.rive.backgroundImagePath})`;
    }

    return '';
  }

  getImageMaxWidth(): string | null {
    return this.data.imageConfig?.maxWidth || null;
  }

  onChatComplete(): void {
    this.isTypingMessagePlayed = true; // ignored for now
  }

  playTextDemo(): void {
    if (this.textPlayer) {
      this.textPlayer.startPlayback();
      this.analyticsService.track('web_text_demo_play_clicked', { asset_id: this.data.assetId });
      this.hasStartedTextDemo = true;
    }
  }

  private checkAnimation(): void {
    if (!this.data || !this.data.rive) {
      return;
    }

    const canvas = this.riveAnimation.nativeElement;
    const rive = this.data.rive!;

    // Small timeout to ensure the canvas is ready
    setTimeout(() => {
      try {
        this.riveInstance = new Rive({
          src: `assets/rive/${rive.fileName}.riv`,
          canvas,
          autoplay: true,
          animations: rive.animations,
          onLoad: () => {
            if (this.riveInstance) {
              this.riveInstance.resizeToCanvas();
            }
          },
        });
      } catch (error) {
        console.error('Failed to initialize Rive animation:', error);
      }
    }, 100);
  }

  private finish(options: string[]): void {
    this.flowService.handleFinishedStep(this.currentIndex, options, this.data);
  }
}
